import { useLatest, useUpdateEffect } from 'ahooks';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { ThemeUICSSObject } from '@apia/theme';
import { useAppSelector } from '../../../../hooks/hooks';
import useModal, { TUseModalConfiguration } from '../../../../hooks/useModal';
import store from '../../../../store';
import { remoteModalsActions } from '../../../../store/remoteModalsSlice';
import { TModify } from '../../../../types';
import { Modal as OuterModal, TModal } from '@apia/components';

export type TRemoteModalChild<DataType> = React.FunctionComponent<{
  data: DataType;
}>;

export type TRemoteOpenedModal<
  DataType,
  ModalType extends typeof OuterModal = typeof OuterModal,
  PrefetcherDataType = unknown,
> = Omit<TModal, 'Portal' | 'children'> &
  TModify<
    TUseModalConfiguration,
    {
      /**
       * Elemento JSX que debe colocarse en un lugar donde esté siempre
       * montado en el DOM de React cuando sea necesario abrir el modal.
       */
      Child: TRemoteModalChild<DataType>;
      id: string;
      /**
       * Permite pasar un tipo de modal distinto que el modal clásico.
       */
      ModalElement?: ModalType;
      preFetcher?: (data?: PrefetcherDataType) => Promise<DataType>;
    }
  > &
  React.ComponentProps<ModalType>;

/**
 * @see **useRemoteModalIsLoading** - Para saber cómo colocar el estado de carga 
 * del modal.
 *
 * Con este método es posible crear un modal que tiene un método que permite
 * abrirlo desde cualquier parte.
 *
 * Admite múltiples propiedades las cuales todas se corresponden con las del
 * hook useModal o el componente Modal que ya tenemos en la aplicación. Cada
 * una de estas propieadades están correctamente documentadas, basta con
 * pararse encima de ellas con el cursor para conocer la documentación.
 *
 * @return - { Modal, open, isLoadingId }
 *
 * **Modal** es un elemento JSX que debe ser colocado donde quede cómodo, pero 
 * que esté montado en la aplicación en todo el ciclo de vida donde sea 
 * necesario poder abrir el modal. Actualmente, este tipo de modales se colocan 
 * en AppModals.
 * 
 * **open** es el método que abrirá el modal.
 * 
 * **isLoadingId** es un identificador que debe ser pasado al hook 
 * **useRemoteModalIsLoading** para saber si este modal está cargando o no.
 * 
 * @example
 * 
 * **DocumentUploadModal.tsx
 * 
  export const {
    Modal: DocumentUploadModal,
    open: openDocumentUploadModal,
    isLoadingId: DocumentUploadModalId,
  } = makeRemoteOpenedModal<string>({
    Child: (props) => {
      return <Box>{props.data}</Box>;
    },
  });   

 * **TestApp.tsx**
  const TestApp = () => {
    const [counter, setCounter] = React.useState(0);

    const isLoading = useRemoteModalIsLoading(DocumentUploadModalId);

    return (
      <>
        <ListNotifications />
        <Box sx={{ padding: 50 }}>
          <SimpleButton
            isLoading={isLoading}
            onClick={() => {
              openDocumentUploadModal(String(counter));
              setCounter((current) => current + 1);
            }}
          >
            Open
          </SimpleButton>
        </Box>
      </>
    );
  };

 * **AppModals.tsx**
  const AppModals = () => {
    ...
    return <>
      ...
      <DocumentUploadModal />
      ...
    </>
  }
 *
 */
export default function makeRemoteOpenedModal<
  DataType = unknown,
  ModalType extends typeof OuterModal = typeof OuterModal,
  PrefetcherDataType = DataType,
>(
  props?: TRemoteOpenedModal<DataType, ModalType, PrefetcherDataType> & {
    sx?: ThemeUICSSObject;
  },
) {
  const ModalElement = props?.ModalElement ?? OuterModal;

  const {
    Child,
    id,
    isDefaultOpen,
    onClose,
    onExited,
    onOpen,
    portalArgs,
    preFetcher: outerPrefetcher,
    ...incomingProps
  } = props ??
  ({} as TRemoteOpenedModal<DataType, ModalType, PrefetcherDataType>);

  const Modal = () => {
    const { className, data, isOpen, size, title } = useAppSelector(
      (global) => {
        const state = global.remoteModalsSlice.modals[id];
        return {
          className: state?.className ?? '',
          isOpen: state?.isOpen ?? false,
          data: state?.data as PrefetcherDataType,
          title: state?.title ?? incomingProps.title,
          size: state?.size ?? incomingProps.size,
        };
      },
      shallowEqual,
    );

    const [prefetchedData, setPrefetchedData] = React.useState<DataType | null>(
      null,
    );

    const latestData = useLatest(data);

    const { show, hide, ...modalProps } = useModal(
      React.useMemo(
        () => ({
          defaultOpen: isDefaultOpen,
          onClose: () => {
            if (onClose) onClose();
            store.dispatch(remoteModalsActions.close(id));
          },
          onExited,
          onOpen,
          portalArgs,
          async preFetcher() {
            store.dispatch(remoteModalsActions.update({ id, isLoading: true }));
            const result = await outerPrefetcher?.(latestData.current);

            if (result) {
              setPrefetchedData(result);
            }
            store.dispatch(
              remoteModalsActions.update({ id, isLoading: false }),
            );
          },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
      ),
    );

    useUpdateEffect(() => {
      if (isOpen) show();
      else hide();
    }, [isOpen]);

    if (data === null) return null;

    return (
      <ModalElement
        {...modalProps}
        {...incomingProps}
        {...((data as Record<string, unknown>)?.modalProps as TModal)}
        title={title}
        size={size}
        className={`${className} ${incomingProps.className ?? ''}`}
      >
        <Child data={prefetchedData ?? (data as unknown as DataType)} />
      </ModalElement>
    );
  };

  return {
    open: (data?: PrefetcherDataType | DataType) =>
      store.dispatch(remoteModalsActions.open({ id, data })),
    Modal,
  };
}
